import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import { staffAPI, confidentialAPI } from '../services/api';

const invalidate = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['terminationRequests'] });
  queryClient.invalidateQueries({ queryKey: ['workPass'] });
};

export const useTerminationCapabilities = (options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'capabilities'],
    queryFn: async () => {
      const response = await staffAPI.getTerminationRequestCapabilities();
      return response.data || {};
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useTerminationRequests = (tab = 'open', options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'list', tab],
    queryFn: async () => {
      const response = await staffAPI.getTerminationRequests({ tab });
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useTerminationRequest = (id, options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'detail', id],
    queryFn: async () => {
      const response = await staffAPI.getTerminationRequest(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 15 * 1000,
    ...options,
  });

export const useEligibleTerminationWorkers = (options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'eligibleWorkers'],
    queryFn: async () => {
      const response = await staffAPI.getTerminationEligibleWorkers();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useResignationNotice = (id, language, options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'notice', id, language],
    queryFn: async () => {
      const response = await staffAPI.getResignationNotice(id, language);
      return response.data;
    },
    enabled: !!id,
    staleTime: 15 * 1000,
    ...options,
  });

export const useCreateAdhocTermination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.createTerminationRequest(data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Termination request submitted for HR review');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not submit request');
    },
  });
};

export const useRecordWorkerIntent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.recordWorkerIntent(data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Notice sent to the worker for acknowledgement');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not record the notice');
    },
  });
};

export const useAcknowledgeResignation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.acknowledgeResignationNotice(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Resignation notice confirmed');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not confirm the notice');
    },
  });
};

export const useDisputeResignation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.disputeResignationNotice(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.warning('HR has been asked to review this notice');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not send the dispute');
    },
  });
};

export const useDepartmentAckTermination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.departmentAcknowledgeTermination(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Notice acknowledged');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not acknowledge');
    },
  });
};

export const useDepartmentReturnTermination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.departmentReturnTermination(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Returned for correction');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not return this notice');
    },
  });
};

export const useCorrectNotice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.correctTerminationNotice(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Corrected notice sent to the worker');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not correct this notice');
    },
  });
};

export const useWitnessedException = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.recordTerminationWitnessedException(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Witnessed acknowledgement recorded');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not record the exception');
    },
  });
};

export const useCancelTermination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.cancelTerminationRequest(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Request cancelled');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not cancel');
    },
  });
};

export const useHRTerminationRequests = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'hr', 'list', params],
    queryFn: async () => {
      const response = await confidentialAPI.getHRTerminationRequests(params);
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useHRTerminationRequest = (id, options = {}) =>
  useQuery({
    queryKey: ['terminationRequests', 'hr', 'detail', id],
    queryFn: async () => {
      const response = await confidentialAPI.getHRTerminationRequest(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 15 * 1000,
    ...options,
  });

export const useApproveTermination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.approveHRTerminationRequest(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      queryClient.invalidateQueries({ queryKey: ['offboarding'] });
      toast.success('Approved and offboarding case created');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not approve this request');
    },
  });
};

export const useRejectTermination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.rejectHRTerminationRequest(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Request rejected');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not reject this request');
    },
  });
};
